import React, { useState, useMemo } from 'react';
import {
  Zap,
  Settings,
  Target,
  TrendingDown,
  Calculator,
  AlertTriangle,
  Info,
  DollarSign,
  Crosshair,
  Shield,
  Calendar,
  Filter,
  BarChart3,
  Activity,
  Clock,
  Database,
  Layers,
  PieChart,
  TrendingUp
} from 'lucide-react';
import { CorrelationAnalysis } from './analytics/CorrelationAnalysis';
import { DistributionAnalysis } from './analytics/DistributionAnalysis';
import { SeasonalityAnalysis } from './analytics/SeasonalityAnalysis';
import { ProbabilisticAnalysis } from './analytics/ProbabilisticAnalysis';
import { RiskAnalytics } from './analytics/RiskAnalytics';
import { CalculatedMetrics } from '../types';
import { safeAverage, calculateStandardDeviation, calculateMedian, formatVolume } from '../utils/calculations';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { toLocalDateString, parseLocalDate } from '../utils/dateHelpers';

interface ShortEntryThresholdCalculatorProps {
  calculatedData: CalculatedMetrics[];
}

// Definizione delle zone di spike
const SPIKE_ZONES = {
  ESTREMA: { label: 'ZONA ESTREMA', min: 200, max: Infinity, color: '#8B0000', bgColor: 'bg-red-900/20', borderColor: 'border-red-800' },
  ALTA: { label: 'ZONA ALTA', min: 100, max: 200, color: '#FF8C00', bgColor: 'bg-orange-900/20', borderColor: 'border-orange-800' },
  MEDIA: { label: 'ZONA MEDIA', min: 50, max: 100, color: '#FFD700', bgColor: 'bg-yellow-900/20', borderColor: 'border-yellow-800' },
  BASE: { label: 'ZONA BASE', min: 30, max: 50, color: '#32CD32', bgColor: 'bg-green-900/20', borderColor: 'border-green-800' },
  BASSA: { label: 'ZONA BASSA', min: 0, max: 30, color: '#D3D3D3', bgColor: 'bg-gray-900/20', borderColor: 'border-gray-800' }
};

// Tipi per i filtri temporali
type TemporalFilter = 'all' | 'last_10' | 'last_20' | 'last_50' | 'last_100' | 'last_200' | 'last_500' | 'custom_range';

interface SpikeStatistics {
  mean: number;
  median: number;
  max: number;
  min: number;
  stdDev: number;
  totalRecords: number;
}

interface ZoneAnalysis {
  zone: string;
  count: number;
  percentage: number;
  avgRetracement: number;
  winRate: number;
  color: string;
  bgColor: string;
  borderColor: string;
}

interface PercentileData {
  percentile: number;
  value: number;
}

interface ReversalProbability {
  zone: string;
  prob10: number;
  prob20: number;
  prob30: number;
  prob50: number;
}

interface SpikeQualityScore {
  ticker: string;
  date: Date;
  sqs: number;
  spikePercent: number;
  volumeRatio: number;
  marketCap: number;
}

type AnalysisTab = 'overview' | 'distribution' | 'correlation' | 'seasonality' | 'probability' | 'risk' | 'calculator';

export const ShortEntryThresholdCalculator: React.FC<ShortEntryThresholdCalculatorProps> = ({
  calculatedData
}) => {
  // Stati per i filtri temporali
  const [temporalFilter, setTemporalFilter] = useState<TemporalFilter>('all');
  const [customStartDate, setCustomStartDate] = useState<Date | null>(null);
  const [customEndDate, setCustomEndDate] = useState<Date | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [currentPrice, setCurrentPrice] = useState<number>(0);
  const [approach, setApproach] = useState<'aggressive' | 'standard' | 'cautious'>('standard');
  const [activeTab, setActiveTab] = useState<AnalysisTab>('overview');

  // Calcola il range di date disponibili
  const dateRange = useMemo(() => {
    if (calculatedData.length === 0) return { min: new Date(), max: new Date() };
    
    const dates = calculatedData.map(row => row.Date).sort((a, b) => a.getTime() - b.getTime());
    return { min: dates[0], max: dates[dates.length - 1] };
  }, [calculatedData]);

  // Filtra i dati in base al filtro temporale selezionato
  const filteredData = useMemo(() => {
    if (!Array.isArray(calculatedData) || calculatedData.length === 0) {
      return [];
    }

    // Ordina i dati per data (più recenti prima)
    const sortedData = [...calculatedData].sort((a, b) => b.Date.getTime() - a.Date.getTime());

    switch (temporalFilter) {
      case 'all':
        return sortedData;
      case 'last_10':
        return sortedData.slice(0, 10);
      case 'last_20':
        return sortedData.slice(0, 20);
      case 'last_50':
        return sortedData.slice(0, 50);
      case 'last_100':
        return sortedData.slice(0, 100);
      case 'last_200':
        return sortedData.slice(0, 200);
      case 'last_500':
        return sortedData.slice(0, 500);
      case 'custom_range':
        return sortedData.filter(row => {
          if (customStartDate && row.Date < customStartDate) return false;
          if (customEndDate && row.Date > customEndDate) return false;
          return true;
        });
      default:
        return sortedData;
    }
  }, [calculatedData, temporalFilter, customStartDate, customEndDate]);

  // Calcola statistiche base degli spike
  const spikeStatistics = useMemo((): SpikeStatistics => {
    if (filteredData.length === 0) {
      return {
        mean: 0,
        median: 0,
        max: 0,
        min: 0,
        stdDev: 0,
        totalRecords: 0
      };
    }

    // Estrai i valori di Spike % (già in formato percentuale)
    const spikeValues = filteredData
      .map(row => (row['Spike %'] || 0) * 100)
      .filter(spike => spike > 0); // Escludi valori 0 per il minimo

    if (spikeValues.length === 0) {
      return {
        mean: 0,
        median: 0,
        max: 0,
        min: 0,
        stdDev: 0,
        totalRecords: filteredData.length
      };
    }

    return {
      mean: safeAverage(spikeValues),
      median: calculateMedian(spikeValues),
      max: Math.max(...spikeValues),
      min: Math.min(...spikeValues),
      stdDev: calculateStandardDeviation(spikeValues),
      totalRecords: filteredData.length
    };
  }, [filteredData]);

  // Calcola distribuzione per zone
  const zoneAnalysis = useMemo((): ZoneAnalysis[] => {
    if (filteredData.length === 0) {
      return Object.values(SPIKE_ZONES).map(zone => ({
        zone: zone.label,
        count: 0,
        percentage: 0,
        avgRetracement: 0,
        winRate: 0,
        color: zone.color,
        bgColor: zone.bgColor,
        borderColor: zone.borderColor
      }));
    }

    return Object.values(SPIKE_ZONES).map(zone => {
      const zoneData = filteredData.filter(row => {
        const spike = (row['Spike %'] || 0) * 100;
        return spike >= zone.min && spike < zone.max;
      });

      if (zoneData.length === 0) {
        return {
          zone: zone.label,
          count: 0,
          percentage: 0,
          avgRetracement: 0,
          winRate: 0,
          color: zone.color,
          bgColor: zone.bgColor,
          borderColor: zone.borderColor
        };
      }

      // Calcola ritracciamento medio (High→Close)
      const retracementValues = zoneData.map(row => {
        const retracement = ((row.Close - row.High) / row.High) * 100;
        return isFinite(retracement) ? retracement : 0;
      });

      // Calcola win rate (% che chiude sotto il high)
      const tradesClosedBelowHigh = zoneData.filter(row => row.Close < row.High).length;
      const winRate = (tradesClosedBelowHigh / zoneData.length) * 100;

      return {
        zone: zone.label,
        count: zoneData.length,
        percentage: (zoneData.length / filteredData.length) * 100,
        avgRetracement: safeAverage(retracementValues),
        winRate,
        color: zone.color,
        bgColor: zone.bgColor,
        borderColor: zone.borderColor
      };
    });
  }, [filteredData]);

  // Calcola percentili chiave
  const percentileData = useMemo((): PercentileData[] => {
    if (filteredData.length === 0) {
      return [50, 75, 90, 95, 99, 99.5, 99.9].map(p => ({ percentile: p, value: 0 }));
    }

    const spikeValues = filteredData
      .map(row => (row['Spike %'] || 0) * 100)
      .sort((a, b) => a - b);

    const percentiles = [50, 75, 90, 95, 99, 99.5, 99.9];
    
    return percentiles.map(p => {
      const index = Math.ceil((p / 100) * spikeValues.length) - 1;
      const value = spikeValues[Math.max(0, Math.min(index, spikeValues.length - 1))] || 0;
      return { percentile: p, value };
    });
  }, [filteredData]);

  // Calcola analisi probabilità reversal
  const reversalProbabilities = useMemo((): ReversalProbability[] => {
    return Object.values(SPIKE_ZONES).map(zone => {
      const zoneData = filteredData.filter(row => {
        const spike = (row['Spike %'] || 0) * 100;
        return spike >= zone.min && spike < zone.max;
      });

      if (zoneData.length === 0) {
        return {
          zone: zone.label,
          prob10: 0,
          prob20: 0,
          prob30: 0,
          prob50: 0
        };
      }

      // Calcola probabilità di ritracciamento per diverse soglie
      const calculateRetracementProb = (threshold: number) => {
        const retracedCount = zoneData.filter(row => {
          const retracement = ((row.Close - row.High) / row.High) * 100;
          return retracement <= -threshold;
        }).length;
        return (retracedCount / zoneData.length) * 100;
      };

      return {
        zone: zone.label,
        prob10: calculateRetracementProb(10),
        prob20: calculateRetracementProb(20),
        prob30: calculateRetracementProb(30),
        prob50: calculateRetracementProb(50)
      };
    });
  }, [filteredData]);

  // Calcola Spike Quality Score (SQS) per tutti i record
  const spikeQualityScores = useMemo((): SpikeQualityScore[] => {
    if (filteredData.length === 0) return [];

    return filteredData
      .map(row => {
        const spikePercent = (row['Spike %'] || 0) * 100;
        const volumeRatio = row['Vol/PM Vol Ratio'] || 0;
        const marketCap = Number(row['Market Cap']) || 1;
        
        // Formula SQS: (Spike% * VolumeRatio) / Math.sqrt(MarketCap/1000000)
        const sqs = volumeRatio > 0 && marketCap > 0 
          ? (spikePercent * volumeRatio) / Math.sqrt(marketCap / 1000000)
          : 0;

        return {
          ticker: row.Ticker,
          date: row.Date,
          sqs: isFinite(sqs) ? sqs : 0,
          spikePercent,
          volumeRatio,
          marketCap
        };
      })
      .filter(item => item.sqs > 0)
      .sort((a, b) => b.sqs - a.sqs);
  }, [filteredData]);

  // Calcola indicatori real-time
  const realTimeIndicators = useMemo(() => {
    if (filteredData.length === 0) {
      return {
        avgSpikeScore: 0,
        globalWinRate: 0,
        avgTheoreticalProfit: 0,
        bestRecentOpportunity: null as SpikeQualityScore | null
      };
    }

    // Spike Score Medio
    const avgSpikeScore = spikeQualityScores.length > 0 
      ? safeAverage(spikeQualityScores.map(item => item.sqs))
      : 0;

    // Win Rate Globale (% che chiude in rosso per short seller)
    const redCloses = filteredData.filter(row => row['Red Close'] === 1).length;
    const globalWinRate = (redCloses / filteredData.length) * 100;

    // Profitto Medio Teorico (ritracciamento medio dal high al close)
    const retracementValues = filteredData.map(row => {
      const retracement = ((row.Close - row.High) / row.High) * 100;
      return isFinite(retracement) ? Math.abs(retracement) : 0;
    });
    const avgTheoreticalProfit = safeAverage(retracementValues);

    // Migliore Opportunità Recente (top SQS negli ultimi 20)
    const recent20 = spikeQualityScores.slice(0, 20);
    const bestRecentOpportunity = recent20.length > 0 ? recent20[0] : null;

    return {
      avgSpikeScore,
      globalWinRate,
      avgTheoreticalProfit,
      bestRecentOpportunity
    };
  }, [filteredData, spikeQualityScores]);

  // Calcola livelli ottimali per short entry
  const shortLevels = useMemo(() => {
    if (currentPrice <= 0 || percentileData.length === 0) {
      return {
        targetSpikePercent: 0,
        stopLossPercent: 0,
        shortEntryPrice: 0,
        stopLossPrice: 0,
        targetProfitPrice: 0,
        riskRewardRatio: 0,
        expectedRetracement: 0
      };
    }

    // Definisci parametri per ogni approccio
    const approachParams = {
      aggressive: { percentileIndex: 1, stopLoss: 2 }, // 75° percentile, 2% stop
      standard: { percentileIndex: 2, stopLoss: 4 },   // 90° percentile, 4% stop
      cautious: { percentileIndex: 3, stopLoss: 6 }    // 95° percentile, 6% stop
    };

    const params = approachParams[approach];
    const targetSpikePercent = percentileData[params.percentileIndex]?.value || 0;
    const stopLossPercent = params.stopLoss;
    
    // Calcola prezzi
    const shortEntryPrice = currentPrice * (1 + targetSpikePercent / 100);
    const stopLossPrice = shortEntryPrice * (1 + stopLossPercent / 100);
    
    // Calcola ritracciamento atteso basato sulla zona
    let expectedRetracement = 15; // Default 15%
    if (targetSpikePercent > 200) expectedRetracement = 25;
    else if (targetSpikePercent > 100) expectedRetracement = 20;
    else if (targetSpikePercent > 50) expectedRetracement = 18;
    else if (targetSpikePercent > 30) expectedRetracement = 15;
    else expectedRetracement = 12;
    
    const targetProfitPrice = shortEntryPrice * (1 - expectedRetracement / 100);
    
    // Calcola R/R ratio
    const risk = stopLossPrice - shortEntryPrice;
    const reward = shortEntryPrice - targetProfitPrice;
    const riskRewardRatio = risk > 0 ? reward / risk : 0;

    return {
      targetSpikePercent,
      stopLossPercent,
      shortEntryPrice,
      stopLossPrice,
      targetProfitPrice,
      riskRewardRatio,
      expectedRetracement
    };
  }, [currentPrice, approach, percentileData]);

  // Funzioni helper per formattare le date
  const formatDateForInput = (date: Date | null) => {
    if (!date) return '';
    return toLocalDateString(date) || '';
  };

  const parseDateFromInput = (dateString: string) => {
    if (!dateString) return null;
    return parseLocalDate(dateString);
  };

  // Funzione per ottenere il colore della zona in base al valore
  const getZoneColor = (spikePercent: number) => {
    for (const zone of Object.values(SPIKE_ZONES)) {
      if (spikePercent >= zone.min && spikePercent < zone.max) {
        return zone.color;
      }
    }
    return SPIKE_ZONES.BASSA.color;
  };

  return (
    <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-4 sm:p-6 mb-6 sm:mb-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 space-y-3 sm:space-y-0">
        <div className="flex items-center space-x-2 sm:space-x-3">
          <div className="p-1.5 sm:p-2 bg-red-400/10 rounded-lg">
            <TrendingDown className="h-4 w-4 sm:h-5 sm:w-5 text-red-400" />
          </div>
          <div>
            <h3 className="text-base sm:text-lg font-semibold text-white">Spike Analysis Dashboard</h3>
            <p className="text-xs sm:text-sm text-gray-400 mt-1">
              Analisi completa dei pattern di spike per strategie di short selling
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2 sm:space-x-3">
          <div className="px-2 sm:px-3 py-1 bg-blue-400/10 text-blue-400 text-xs sm:text-sm rounded-full border border-blue-400/30">
            {spikeStatistics.totalRecords} Record Analizzati
          </div>
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center space-x-2 text-xs sm:text-sm text-gray-400 hover:text-white transition-colors"
          >
            <Settings className="h-3 w-3 sm:h-4 sm:w-4" />
            <span>{showAdvanced ? 'Nascondi' : 'Mostra'} Avanzate</span>
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-gray-900/30 border border-gray-600 rounded-lg p-3 mb-6">
        <div className="flex items-center space-x-2 mb-3">
          <Layers className="h-4 w-4 text-purple-400" />
          <span className="text-sm font-medium text-white">Sezioni di Analisi</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            { id: 'overview', label: 'Panoramica', icon: Activity },
            { id: 'distribution', label: 'Distribuzione', icon: BarChart3 },
            { id: 'correlation', label: 'Correlazioni', icon: TrendingUp },
            { id: 'seasonality', label: 'Stagionalità', icon: Calendar },
            { id: 'probability', label: 'Probabilità', icon: Target },
            { id: 'risk', label: 'Risk Analytics', icon: Shield },
            { id: 'calculator', label: 'Calcolatore', icon: Calculator }
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as AnalysisTab)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-purple-600 text-white shadow-lg'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Filtri Temporali Dinamici */}
      <div className="bg-gray-900/30 border border-gray-600 rounded-lg p-4 mb-6">
        <div className="flex items-center space-x-2 sm:space-x-3 mb-4">
          <div className="p-1.5 sm:p-2 bg-blue-400/10 rounded-lg">
            <Filter className="h-4 w-4 sm:h-5 sm:w-5 text-blue-400" />
          </div>
          <div>
            <h4 className="text-sm sm:text-base font-semibold text-white">Filtri Temporali</h4>
            <p className="text-xs text-gray-400">Seleziona il periodo di analisi</p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 sm:gap-3 mb-4">
          {[
            { value: 'all', label: 'Tutti' },
            { value: 'last_10', label: 'Ultimi 10' },
            { value: 'last_20', label: 'Ultimi 20' },
            { value: 'last_50', label: 'Ultimi 50' },
            { value: 'last_100', label: 'Ultimi 100' },
            { value: 'last_200', label: 'Ultimi 200' },
            { value: 'last_500', label: 'Ultimi 500' },
            { value: 'custom_range', label: 'Personalizzato' }
          ].map(option => (
            <button
              key={option.value}
              onClick={() => setTemporalFilter(option.value as TemporalFilter)}
              className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                temporalFilter === option.value
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        {/* Date Range Personalizzato */}
        {temporalFilter === 'custom_range' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-600">
            <div className="space-y-2">
              <label className="flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm font-medium text-gray-300">
                <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
                <span>Data Inizio</span>
              </label>
              <input
                type="date"
                value={formatDateForInput(customStartDate)}
                onChange={(e) => setCustomStartDate(parseDateFromInput(e.target.value))}
                min={formatDateForInput(dateRange.min)}
                max={formatDateForInput(dateRange.max)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
            <div className="space-y-2">
              <label className="flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm font-medium text-gray-300">
                <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
                <span>Data Fine</span>
              </label>
              <input
                type="date"
                value={formatDateForInput(customEndDate)}
                onChange={(e) => setCustomEndDate(parseDateFromInput(e.target.value))}
                min={formatDateForInput(dateRange.min)}
                max={formatDateForInput(dateRange.max)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
          </div>
        )}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <>
          {/* Indicatori Real-Time (Cards in alto) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6">
        {/* Spike Score Medio */}
        <div className="bg-gray-900/30 rounded-lg p-4 border border-gray-600">
          <div className="flex items-center space-x-2 mb-2">
            <Zap className="h-4 w-4 text-purple-400" />
            <p className="text-xs sm:text-sm font-medium text-gray-300">Spike Score Medio</p>
          </div>
          <p className="text-xl sm:text-2xl font-bold text-purple-400">
            {realTimeIndicators.avgSpikeScore.toFixed(2)}
          </p>
          <p className="text-xs text-gray-500 mt-1">SQS medio del periodo</p>
        </div>

        {/* Win Rate Globale */}
        <div className="bg-gray-900/30 rounded-lg p-4 border border-gray-600">
          <div className="flex items-center space-x-2 mb-2">
            <Target className="h-4 w-4 text-green-400" />
            <p className="text-xs sm:text-sm font-medium text-gray-300">Win Rate Short</p>
          </div>
          <p className="text-xl sm:text-2xl font-bold text-green-400">
            {realTimeIndicators.globalWinRate.toFixed(1)}%
          </p>
          <p className="text-xs text-gray-500 mt-1">% chiusure rosse (favorevoli short)</p>
        </div>

        {/* Profitto Medio Teorico */}
        <div className="bg-gray-900/30 rounded-lg p-4 border border-gray-600">
          <div className="flex items-center space-x-2 mb-2">
            <DollarSign className="h-4 w-4 text-yellow-400" />
            <p className="text-xs sm:text-sm font-medium text-gray-300">Ritracciamento Medio</p>
          </div>
          <p className="text-xl sm:text-2xl font-bold text-yellow-400">
            {realTimeIndicators.avgTheoreticalProfit.toFixed(1)}%
          </p>
          <p className="text-xs text-gray-500 mt-1">High → Close medio</p>
        </div>

        {/* Migliore Opportunità Recente */}
        <div className="bg-gray-900/30 rounded-lg p-4 border border-gray-600">
          <div className="flex items-center space-x-2 mb-2">
            <Activity className="h-4 w-4 text-blue-400" />
            <p className="text-xs sm:text-sm font-medium text-gray-300">Top SQS Recente</p>
          </div>
          {realTimeIndicators.bestRecentOpportunity ? (
            <>
              <p className="text-xl sm:text-2xl font-bold text-blue-400">
                {realTimeIndicators.bestRecentOpportunity.sqs.toFixed(1)}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {realTimeIndicators.bestRecentOpportunity.ticker} - {realTimeIndicators.bestRecentOpportunity.spikePercent.toFixed(1)}%
              </p>
            </>
          ) : (
            <p className="text-xl sm:text-2xl font-bold text-gray-400">N/A</p>
          )}
        </div>
      </div>

      {/* Statistiche Base Spike */}
      <div className="bg-gray-900/30 border border-gray-600 rounded-lg p-4 sm:p-6 mb-6">
        <div className="flex items-center space-x-2 sm:space-x-3 mb-4">
          <div className="p-1.5 sm:p-2 bg-green-400/10 rounded-lg">
            <BarChart3 className="h-4 w-4 sm:h-5 sm:w-5 text-green-400" />
          </div>
          <div>
            <h4 className="text-sm sm:text-base font-semibold text-white">Statistiche Base Spike</h4>
            <p className="text-xs text-gray-400">Metriche fondamentali del periodo selezionato</p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="text-center">
            <p className="text-lg sm:text-xl font-bold text-blue-400">{spikeStatistics.mean.toFixed(2)}%</p>
            <p className="text-xs text-gray-400">Media Spike</p>
          </div>
          <div className="text-center">
            <p className="text-lg sm:text-xl font-bold text-green-400">{spikeStatistics.median.toFixed(2)}%</p>
            <p className="text-xs text-gray-400">Mediana Spike</p>
          </div>
          <div className="text-center">
            <p className="text-lg sm:text-xl font-bold text-red-400">{spikeStatistics.max.toFixed(2)}%</p>
            <p className="text-xs text-gray-400">Spike Massimo</p>
          </div>
          <div className="text-center">
            <p className="text-lg sm:text-xl font-bold text-yellow-400">{spikeStatistics.min.toFixed(2)}%</p>
            <p className="text-xs text-gray-400">Spike Minimo</p>
          </div>
          <div className="text-center">
            <p className="text-lg sm:text-xl font-bold text-purple-400">{spikeStatistics.stdDev.toFixed(2)}%</p>
            <p className="text-xs text-gray-400">Deviazione Std</p>
          </div>
          <div className="text-center">
            <p className="text-lg sm:text-xl font-bold text-white">{spikeStatistics.totalRecords}</p>
            <p className="text-xs text-gray-400">Record Totali</p>
          </div>
        </div>
      </div>

      {/* Distribuzione per Zone */}
      <div className="mb-6">
        <div className="flex items-center space-x-2 sm:space-x-3 mb-4">
          <div className="p-1.5 sm:p-2 bg-orange-400/10 rounded-lg">
            <Target className="h-4 w-4 sm:h-5 sm:w-5 text-orange-400" />
          </div>
          <div>
            <h4 className="text-sm sm:text-base font-semibold text-white">Distribuzione per Zone di Spike</h4>
            <p className="text-xs text-gray-400">Analisi dettagliata per ogni fascia di volatilità</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {zoneAnalysis.map((zone, index) => (
            <div 
              key={index}
              className={`border rounded-lg p-4 ${zone.bgColor} ${zone.borderColor}`}
            >
              <div className="text-center mb-3">
                <h5 className="text-sm font-bold text-white mb-1">{zone.zone}</h5>
                <div 
                  className="w-4 h-4 rounded-full mx-auto mb-2"
                  style={{ backgroundColor: zone.color }}
                ></div>
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-400">Casi:</span>
                  <span className="text-sm font-bold text-white">{zone.count}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-400">% Totale:</span>
                  <span className="text-sm font-bold text-blue-400">{zone.percentage.toFixed(1)}%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-400">Ritracciamento:</span>
                  <span className="text-sm font-bold text-yellow-400">{zone.avgRetracement.toFixed(1)}%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-400">Win Rate:</span>
                  <span className="text-sm font-bold text-green-400">{zone.winRate.toFixed(1)}%</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Percentili Chiave */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-gray-900/30 border border-gray-600 rounded-lg p-4">
          <div className="flex items-center space-x-2 sm:space-x-3 mb-4">
            <div className="p-1.5 sm:p-2 bg-yellow-400/10 rounded-lg">
              <BarChart3 className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-400" />
            </div>
            <div>
              <h4 className="text-sm sm:text-base font-semibold text-white">Percentili Chiave</h4>
              <p className="text-xs text-gray-400">Distribuzione statistica degli spike</p>
            </div>
          </div>

          <div className="space-y-2">
            {percentileData.map((item, index) => (
              <div key={index} className="flex justify-between items-center py-1">
                <span className="text-sm text-gray-400">{item.percentile}° percentile:</span>
                <span className="text-sm font-bold text-white">{item.value.toFixed(2)}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* Analisi Probabilità Reversal */}
        <div className="bg-gray-900/30 border border-gray-600 rounded-lg p-4">
          <div className="flex items-center space-x-2 sm:space-x-3 mb-4">
            <div className="p-1.5 sm:p-2 bg-red-400/10 rounded-lg">
              <TrendingDown className="h-4 w-4 sm:h-5 sm:w-5 text-red-400" />
            </div>
            <div>
              <h4 className="text-sm sm:text-base font-semibold text-white">Probabilità Reversal</h4>
              <p className="text-xs text-gray-400">Probabilità di ritracciamento per zona</p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-600">
                  <th className="text-left py-2 text-gray-400">Zona</th>
                  <th className="text-center py-2 text-gray-400">-10%</th>
                  <th className="text-center py-2 text-gray-400">-20%</th>
                  <th className="text-center py-2 text-gray-400">-30%</th>
                  <th className="text-center py-2 text-gray-400">-50%</th>
                </tr>
              </thead>
              <tbody>
                {reversalProbabilities.map((prob, index) => (
                  <tr key={index} className="border-b border-gray-700">
                    <td className="py-2 text-white font-medium">{prob.zone}</td>
                    <td className="text-center py-2 text-green-400">{prob.prob10.toFixed(1)}%</td>
                    <td className="text-center py-2 text-yellow-400">{prob.prob20.toFixed(1)}%</td>
                    <td className="text-center py-2 text-orange-400">{prob.prob30.toFixed(1)}%</td>
                    <td className="text-center py-2 text-red-400">{prob.prob50.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>


      {/* Sezioni Avanzate */}
      {showAdvanced && (
        <div className="space-y-6">
          {/* Informazioni sul Dataset */}
          <div className="bg-blue-400/10 border border-blue-400/30 rounded-lg p-4">
            <div className="flex items-center space-x-2 sm:space-x-3 mb-3">
              <Database className="h-4 w-4 sm:h-5 sm:w-5 text-blue-400" />
              <h4 className="text-sm font-medium text-blue-400">Informazioni Dataset</h4>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
              <div>
                <span className="text-gray-400">Periodo Analizzato:</span>
                <p className="text-white font-medium">
                  {temporalFilter === 'custom_range' && customStartDate && customEndDate
                    ? `${format(customStartDate, 'dd/MM/yyyy', { locale: it })} - ${format(customEndDate, 'dd/MM/yyyy', { locale: it })}`
                    : temporalFilter === 'all'
                    ? `${format(dateRange.min, 'dd/MM/yyyy', { locale: it })} - ${format(dateRange.max, 'dd/MM/yyyy', { locale: it })}`
                    : `Ultimi ${temporalFilter.split('_')[1]} spike`
                  }
                </p>
              </div>
              <div>
                <span className="text-gray-400">Record Filtrati:</span>
                <p className="text-white font-medium">{spikeStatistics.totalRecords}</p>
              </div>
              <div>
                <span className="text-gray-400">Ticker Unici:</span>
                <p className="text-white font-medium">
                  {new Set(filteredData.map(row => row.Ticker)).size}
                </p>
              </div>
              <div>
                <span className="text-gray-400">Ultimo Aggiornamento:</span>
                <p className="text-white font-medium">{new Date().toLocaleTimeString('it-IT')}</p>
              </div>
            </div>
          </div>

          {/* Spiegazione Formula SQS */}
          <div className="bg-gray-700/20 border border-gray-600/50 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-3">
              <Calculator className="h-4 w-4 text-gray-400" />
              <h4 className="text-sm font-medium text-gray-300">Formula Spike Quality Score (SQS)</h4>
            </div>
            <div className="space-y-2 text-xs sm:text-sm text-gray-400">
              <div>
                <strong className="text-gray-300">SQS = (Spike% × Volume Ratio) ÷ √(Market Cap ÷ 1M)</strong>
              </div>
              <div>
                <strong className="text-gray-300">Win Rate per Zona:</strong> (Trades Chiusi Sotto High ÷ Trades Totali Zona) × 100
              </div>
              <div>
                <strong className="text-gray-300">Ritracciamento %:</strong> ((Close - High) ÷ High) × 100
              </div>
              <div>
                <strong className="text-gray-300">Probabilità Ritracciamento X%:</strong> (Trades con Ritracciamento ≥ X% ÷ Trades Totali) × 100
              </div>
              <div className="text-xs text-gray-500 mt-2">
                L'SQS combina intensità dello spike, liquidità (volume ratio) e dimensione dell'azienda per identificare le migliori opportunità di short selling.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Top Spike Table */}
      <div className="bg-gray-900/30 border border-gray-600 rounded-lg p-4 mb-6">
        <div className="flex items-center space-x-2 sm:space-x-3 mb-4">
          <div className="p-1.5 sm:p-2 bg-blue-400/10 rounded-lg">
            <BarChart3 className="h-4 w-4 sm:h-5 sm:w-5 text-blue-400" />
          </div>
          <div>
            <h4 className="text-sm sm:text-base font-semibold text-white">Top 20 Spike Più Alti</h4>
            <p className="text-xs text-gray-400">I 20 spike più significativi nel periodo selezionato</p>
          </div>
        </div>

        <div className="overflow-x-auto">
        <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
          <table className="w-full min-w-[800px]">
            <thead className="bg-gray-800/50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-300 uppercase">Rank</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-300 uppercase">Ticker</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-300 uppercase">Data</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-300 uppercase">Spike %</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-300 uppercase">Volume</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-300 uppercase">High→Close %</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-300 uppercase">Chiusura Rossa</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-300 uppercase">Volume Ratio</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {filteredData
                .sort((a, b) => ((b['Spike %'] || 0) * 100) - ((a['Spike %'] || 0) * 100))
                .slice(0, 20)
                .map((row, index) => {
                  const spikePercent = (row['Spike %'] || 0) * 100;
                  const highToClosePercent = ((row.Close - row.High) / row.High) * 100;
                  const isRedClose = row['Red Close'] === 1;
                  const volumeRatio = row['Vol/PM Vol Ratio'] || 0;
                  
                  return (
                    <tr key={index} className="hover:bg-gray-800/30 transition-colors">
                      <td className="px-3 py-2 text-sm text-white font-bold">#{index + 1}</td>
                      <td className="px-3 py-2 text-sm font-medium text-blue-400">{row.Ticker}</td>
                      <td className="px-3 py-2 text-sm text-gray-300">
                        {format(row.Date, 'dd/MM/yyyy', { locale: it })}
                      </td>
                      <td className="px-3 py-2 text-sm font-bold" style={{ color: getZoneColor(spikePercent) }}>
                        {spikePercent.toFixed(1)}%
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-300">
                        {formatVolume(row.Volume)}
                      </td>
                      <td className="px-3 py-2 text-sm font-bold text-red-400">
                        {highToClosePercent.toFixed(1)}%
                      </td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          isRedClose 
                            ? 'bg-red-400/10 text-red-400' 
                            : 'bg-green-400/10 text-green-400'
                        }`}>
                          {isRedClose ? 'Sì' : 'No'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-sm text-green-400">
                        {volumeRatio.toFixed(1)}x
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
        </div>
      </div>

      {/* Sistema di Alert/Suggerimenti */}
      <div className="bg-yellow-400/10 border border-yellow-400/30 rounded-lg p-4 mb-6">
        <div className="flex items-center space-x-2 sm:space-x-3 mb-4">
          <div className="p-1.5 sm:p-2 bg-yellow-400/10 rounded-lg">
            <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-400" />
          </div>
          <div>
            <h4 className="text-sm sm:text-base font-semibold text-white">Alert e Suggerimenti di Trading</h4>
            <p className="text-xs text-gray-400">Analisi automatica delle condizioni di mercato</p>
          </div>
        </div>

        <div className="space-y-3">
          {/* Alert Volatilità */}
          {spikeStatistics.mean > (calculatedData.reduce((sum, row) => sum + ((row['Spike %'] || 0) * 100), 0) / calculatedData.length) * 1.5 && (
            <div className="flex items-start space-x-3 p-3 bg-red-900/20 border border-red-800 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <h5 className="text-sm font-medium text-red-400">⚠️ Mercato in Fase di Alta Volatilità</h5>
                <p className="text-xs text-red-300 mt-1">
                  Spike medio attuale ({spikeStatistics.mean.toFixed(1)}%) è significativamente sopra la media storica. 
                  Considera di aumentare gli stop loss e ridurre la size delle posizioni.
                </p>
              </div>
            </div>
          )}

          {/* Alert Win Rate */}
          {realTimeIndicators.globalWinRate < 70 && (
            <div className="flex items-start space-x-3 p-3 bg-orange-900/20 border border-orange-800 rounded-lg">
              <TrendingDown className="h-5 w-5 text-orange-400 flex-shrink-0 mt-0.5" />
              <div>
                <h5 className="text-sm font-medium text-orange-400">📉 Win Rate Sotto Media</h5>
                <p className="text-xs text-orange-300 mt-1">
                  Win rate attuale ({realTimeIndicators.globalWinRate.toFixed(1)}%) è sotto la soglia ottimale del 70%. 
                  Riduci il position sizing e sii più selettivo negli entry.
                </p>
              </div>
            </div>
          )}

          {/* Alert Spike Estremo */}
          {spikeStatistics.max > 500 && (
            <div className="flex items-start space-x-3 p-3 bg-purple-900/20 border border-purple-800 rounded-lg">
              <Zap className="h-5 w-5 text-purple-400 flex-shrink-0 mt-0.5" />
              <div>
                <h5 className="text-sm font-medium text-purple-400">🔥 Spike Estremo Rilevato</h5>
                <p className="text-xs text-purple-300 mt-1">
                  Spike massimo di {spikeStatistics.max.toFixed(1)}% rilevato nel periodo. 
                  Opportunità short ad alto potenziale - monitora per entry su pullback.
                </p>
              </div>
            </div>
          )}

          {/* Alert Condizioni Normali */}
          {spikeStatistics.mean <= (calculatedData.reduce((sum, row) => sum + ((row['Spike %'] || 0) * 100), 0) / calculatedData.length) * 1.5 && 
           realTimeIndicators.globalWinRate >= 70 && 
           spikeStatistics.max <= 500 && (
            <div className="flex items-start space-x-3 p-3 bg-green-900/20 border border-green-800 rounded-lg">
              <Target className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
              <div>
                <h5 className="text-sm font-medium text-green-400">✅ Condizioni di Mercato Favorevoli</h5>
                <p className="text-xs text-green-300 mt-1">
                  Volatilità nella norma, win rate sopra il 70% e nessun spike estremo. 
                  Condizioni ideali per strategie short standard.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Calcolatore Livelli Ottimali Short Entry/Stop Loss */}
      <div className="bg-gray-900/30 border border-gray-600 rounded-lg p-4 sm:p-6 mb-6">
        <div className="flex items-center space-x-2 sm:space-x-3 mb-4 sm:mb-6">
          <div className="p-1.5 sm:p-2 bg-red-400/10 rounded-lg">
            <Crosshair className="h-4 w-4 sm:h-5 sm:w-5 text-red-400" />
          </div>
          <div>
            <h4 className="text-base sm:text-lg font-semibold text-white">Calcolatore Livelli Ottimali Short</h4>
            <p className="text-xs sm:text-sm text-gray-400">Calcola entry e stop loss ottimali basati sui pattern storici</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input Section */}
          <div className="space-y-4">
            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-300 mb-2">
                <DollarSign className="h-4 w-4" />
                <span>Prezzo di Apertura Attuale ($)</span>
              </label>
              <input
                type="number"
                step="0.01"
                value={currentPrice || ''}
                onChange={(e) => setCurrentPrice(Number(e.target.value))}
                placeholder="es. 5.25"
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-400"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">
                Approccio di Trading
              </label>
              <div className="space-y-2">
                <label className="flex items-center space-x-3 p-3 bg-gray-800/50 rounded-lg border border-gray-600 hover:bg-gray-800/70 transition-colors cursor-pointer">
                  <input
                    type="radio"
                    name="approach"
                    value="aggressive"
                    checked={approach === 'aggressive'}
                    onChange={(e) => setApproach(e.target.value as 'aggressive' | 'standard' | 'cautious')}
                    className="text-red-400 focus:ring-red-400"
                  />
                  <div>
                    <span className="text-sm font-medium text-white">Aggressivo</span>
                    <p className="text-xs text-gray-400">Entry al 75° percentile, stop loss 2%</p>
                  </div>
                </label>
                <label className="flex items-center space-x-3 p-3 bg-gray-800/50 rounded-lg border border-gray-600 hover:bg-gray-800/70 transition-colors cursor-pointer">
                  <input
                    type="radio"
                    name="approach"
                    value="standard"
                    checked={approach === 'standard'}
                    onChange={(e) => setApproach(e.target.value as 'aggressive' | 'standard' | 'cautious')}
                    className="text-red-400 focus:ring-red-400"
                  />
                  <div>
                    <span className="text-sm font-medium text-white">Standard</span>
                    <p className="text-xs text-gray-400">Entry al 90° percentile, stop loss 4%</p>
                  </div>
                </label>
                <label className="flex items-center space-x-3 p-3 bg-gray-800/50 rounded-lg border border-gray-600 hover:bg-gray-800/70 transition-colors cursor-pointer">
                  <input
                    type="radio"
                    name="approach"
                    value="cautious"
                    checked={approach === 'cautious'}
                    onChange={(e) => setApproach(e.target.value as 'aggressive' | 'standard' | 'cautious')}
                    className="text-red-400 focus:ring-red-400"
                  />
                  <div>
                    <span className="text-sm font-medium text-white">Cauto</span>
                    <p className="text-xs text-gray-400">Entry al 95° percentile, stop loss 6%</p>
                  </div>
                </label>
              </div>
            </div>
          </div>

          {/* Output Section */}
          <div className="space-y-4">
            {currentPrice > 0 && (
              <>
                <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-600">
                  <h5 className="text-sm font-medium text-white mb-3 flex items-center space-x-2">
                    <Target className="h-4 w-4 text-red-400" />
                    <span>Livelli Calcolati - Approccio {approach.charAt(0).toUpperCase() + approach.slice(1)}</span>
                  </h5>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-2 bg-red-900/20 rounded border border-red-800">
                      <span className="text-sm text-gray-300">Prezzo Entry Short:</span>
                      <div className="text-right">
                        <span className="text-lg font-bold text-red-400">${shortLevels.shortEntryPrice.toFixed(2)}</span>
                        <p className="text-xs text-gray-400">+{shortLevels.targetSpikePercent.toFixed(1)}% dal prezzo attuale</p>
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center p-2 bg-orange-900/20 rounded border border-orange-800">
                      <span className="text-sm text-gray-300">Stop Loss:</span>
                      <div className="text-right">
                        <span className="text-lg font-bold text-orange-400">${shortLevels.stopLossPrice.toFixed(2)}</span>
                        <p className="text-xs text-gray-400">+{shortLevels.stopLossPercent}% dall'entry</p>
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center p-2 bg-green-900/20 rounded border border-green-800">
                      <span className="text-sm text-gray-300">Target Profitto:</span>
                      <div className="text-right">
                        <span className="text-lg font-bold text-green-400">${shortLevels.targetProfitPrice.toFixed(2)}</span>
                        <p className="text-xs text-gray-400">{shortLevels.expectedRetracement.toFixed(1)}% ritracciamento atteso</p>
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center p-2 bg-blue-900/20 rounded border border-blue-800">
                      <span className="text-sm text-gray-300">Rapporto R/R:</span>
                      <div className="text-right">
                        <span className="text-lg font-bold text-blue-400">{shortLevels.riskRewardRatio.toFixed(2)}:1</span>
                        <p className="text-xs text-gray-400">
                          {shortLevels.riskRewardRatio >= 2 ? 'Eccellente' : shortLevels.riskRewardRatio >= 1.5 ? 'Buono' : 'Accettabile'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-400/10 border border-blue-400/30 rounded-lg p-3">
                  <div className="flex items-start space-x-2">
                    <Info className="h-4 w-4 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h6 className="text-sm font-medium text-blue-400">Come Utilizzare</h6>
                      <ul className="text-xs text-blue-300 mt-1 space-y-1">
                        <li>• Attendi che il prezzo raggiunga il livello di entry calcolato</li>
                        <li>• Posiziona lo stop loss al livello indicato</li>
                        <li>• Prendi profitti parziali al target suggerito</li>
                        <li>• Monitora il volume per conferma del movimento</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </>
            )}
            
            {currentPrice === 0 && (
              <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-600 text-center">
                <Calculator className="h-8 w-8 text-gray-500 mx-auto mb-3" />
                <h5 className="text-sm font-medium text-white mb-2">Inserisci Prezzo di Apertura</h5>
                <p className="text-xs text-gray-400">
                  Inserisci il prezzo di apertura attuale per calcolare i livelli ottimali di entry e stop loss
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
        </>
      )}

      {activeTab === 'distribution' && (
        <div className="mt-6">
          <DistributionAnalysis data={filteredData} />
        </div>
      )}

      {activeTab === 'correlation' && (
        <div className="mt-6">
          <CorrelationAnalysis data={filteredData} />
        </div>
      )}

      {activeTab === 'seasonality' && (
        <div className="mt-6">
          <SeasonalityAnalysis data={filteredData} />
        </div>
      )}

      {activeTab === 'probability' && (
        <div className="mt-6">
          <ProbabilisticAnalysis data={filteredData} spikeZones={SPIKE_ZONES} />
        </div>
      )}

      {activeTab === 'risk' && (
        <div className="mt-6">
          <RiskAnalytics data={filteredData} />
        </div>
      )}

      {activeTab === 'calculator' && (
        <div className="mt-6">
          <div className="bg-gray-900/30 border border-gray-600 rounded-lg p-4 sm:p-6 mb-6">
            <div className="flex items-center space-x-2 sm:space-x-3 mb-4 sm:mb-6">
              <div className="p-1.5 sm:p-2 bg-red-400/10 rounded-lg">
                <Crosshair className="h-4 w-4 sm:h-5 sm:w-5 text-red-400" />
              </div>
              <div>
                <h4 className="text-base sm:text-lg font-semibold text-white">Calcolatore Livelli Ottimali Short</h4>
                <p className="text-xs sm:text-sm text-gray-400">Calcola entry e stop loss ottimali basati sui pattern storici</p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-300 mb-2">
                    <DollarSign className="h-4 w-4" />
                    <span>Prezzo di Apertura Attuale ($)</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={currentPrice || ''}
                    onChange={(e) => setCurrentPrice(Number(e.target.value))}
                    placeholder="es. 5.25"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-400"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-3">
                    Approccio di Trading
                  </label>
                  <div className="space-y-2">
                    {[
                      { value: 'aggressive', label: 'Aggressivo', desc: 'Entry al 75° percentile, stop loss 2%' },
                      { value: 'standard', label: 'Standard', desc: 'Entry al 90° percentile, stop loss 4%' },
                      { value: 'cautious', label: 'Cauto', desc: 'Entry al 95° percentile, stop loss 6%' }
                    ].map(option => (
                      <label key={option.value} className="flex items-center space-x-3 p-3 bg-gray-800/50 rounded-lg border border-gray-600 hover:bg-gray-800/70 transition-colors cursor-pointer">
                        <input
                          type="radio"
                          name="approach"
                          value={option.value}
                          checked={approach === option.value}
                          onChange={(e) => setApproach(e.target.value as 'aggressive' | 'standard' | 'cautious')}
                          className="text-red-400 focus:ring-red-400"
                        />
                        <div>
                          <span className="text-sm font-medium text-white">{option.label}</span>
                          <p className="text-xs text-gray-400">{option.desc}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                {currentPrice > 0 ? (
                  <>
                    <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-600">
                      <h5 className="text-sm font-medium text-white mb-3 flex items-center space-x-2">
                        <Target className="h-4 w-4 text-red-400" />
                        <span>Livelli Calcolati - Approccio {approach.charAt(0).toUpperCase() + approach.slice(1)}</span>
                      </h5>

                      <div className="space-y-3">
                        <div className="flex justify-between items-center p-2 bg-red-900/20 rounded border border-red-800">
                          <span className="text-sm text-gray-300">Prezzo Entry Short:</span>
                          <div className="text-right">
                            <span className="text-lg font-bold text-red-400">${shortLevels.shortEntryPrice.toFixed(2)}</span>
                            <p className="text-xs text-gray-400">+{shortLevels.targetSpikePercent.toFixed(1)}% dal prezzo attuale</p>
                          </div>
                        </div>

                        <div className="flex justify-between items-center p-2 bg-orange-900/20 rounded border border-orange-800">
                          <span className="text-sm text-gray-300">Stop Loss:</span>
                          <div className="text-right">
                            <span className="text-lg font-bold text-orange-400">${shortLevels.stopLossPrice.toFixed(2)}</span>
                            <p className="text-xs text-gray-400">+{shortLevels.stopLossPercent}% dall'entry</p>
                          </div>
                        </div>

                        <div className="flex justify-between items-center p-2 bg-green-900/20 rounded border border-green-800">
                          <span className="text-sm text-gray-300">Target Profitto:</span>
                          <div className="text-right">
                            <span className="text-lg font-bold text-green-400">${shortLevels.targetProfitPrice.toFixed(2)}</span>
                            <p className="text-xs text-gray-400">{shortLevels.expectedRetracement.toFixed(1)}% ritracciamento atteso</p>
                          </div>
                        </div>

                        <div className="flex justify-between items-center p-2 bg-blue-900/20 rounded border border-blue-800">
                          <span className="text-sm text-gray-300">Rapporto R/R:</span>
                          <div className="text-right">
                            <span className="text-lg font-bold text-blue-400">{shortLevels.riskRewardRatio.toFixed(2)}:1</span>
                            <p className="text-xs text-gray-400">
                              {shortLevels.riskRewardRatio >= 2 ? 'Eccellente' : shortLevels.riskRewardRatio >= 1.5 ? 'Buono' : 'Accettabile'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-blue-400/10 border border-blue-400/30 rounded-lg p-3">
                      <div className="flex items-start space-x-2">
                        <Info className="h-4 w-4 text-blue-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <h6 className="text-sm font-medium text-blue-400">Come Utilizzare</h6>
                          <ul className="text-xs text-blue-300 mt-1 space-y-1">
                            <li>• Attendi che il prezzo raggiunga il livello di entry calcolato</li>
                            <li>• Posiziona lo stop loss al livello indicato</li>
                            <li>• Prendi profitti parziali al target suggerito</li>
                            <li>• Monitora il volume per conferma del movimento</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-600 text-center">
                    <Calculator className="h-8 w-8 text-gray-500 mx-auto mb-3" />
                    <h5 className="text-sm font-medium text-white mb-2">Inserisci Prezzo di Apertura</h5>
                    <p className="text-xs text-gray-400">
                      Inserisci il prezzo di apertura attuale per calcolare i livelli ottimali di entry e stop loss
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};